import {
  Alert,
  AnimatedModal,
  Button,
  EAlertSize,
  EAlertVariant,
  EButtonColorVariant,
  EHeadingStyleVariant,
  EHelpTextVariant,
  EModalFooterVariants,
  EModalSize,
  EModalVariants,
  ETextStyleVariant,
  GeneralBulbIcon,
  Heading,
  HelpText,
  LinkButton,
  Modal,
  Text,
  Textarea,
} from '@outdoorsyco/bonfire';
import pick from 'lodash/pick';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useBreakpointContext } from '@/components/common/BreakpointContext';
import CategoryRating from '@/components/reviews/CategoryRating';
import ImageUploader from '@/components/reviews/ImageUploader';
import StarRating from '@/components/route/search/StarRating/StarRating';
import Loading from '@/components/switchback/Loading/Loading';
import { selectAuthenticated } from '@/redux/modules/auth/selectors';
import { fetchBooking } from '@/redux/modules/booking/actions';
import {
  bookingDetailsSelector,
  loadBookingDetailsSelector,
} from '@/redux/modules/booking/selectors';
import { fetchReviewByBookingId } from '@/redux/modules/reviews';
import { getAuthenticatedUser, getIsFetchingUser } from '@/redux/selectors/auth/user';
import { getCurrentReview } from '@/redux/selectors/listing/reviews';
import {
  countStepOneReviewFieldChanged,
  trackReviewModalOpened,
  trackReviewRated,
  trackReviewStepAbandoned,
  trackReviewStepCompleted,
  trackReviewSubmit,
} from '@/services/analytics/reviews';
import {
  EReviewFlow,
  EReviewSource,
  REVIEW_STEP_NAMES,
  TReviewStepName,
} from '@/services/analytics/reviews/types';
import { ERentalType } from '@/services/analytics/types';
import apiRequest from '@/services/apiRequest';
import { IReview } from '@/services/types/core/reviews/id';
import { getCoreApi } from '@/utility/getCoreApi';
import { getUrlParams } from '@/utility/queryParams';
import { triggerLoginModal } from '@/utility/triggerLoginModal';

import { useReviewExpiredMessage } from '../reviews/useReviewExpiredMessage/useReviewExpiredMessage';

interface IReviewTemplate {
  bookingId: number;
  isOpen: boolean;
  onClose: () => void;
}

const TOTAL_STEPS = REVIEW_STEP_NAMES.length;
const CHAR_MIN = 10;
const CHAR_LIMIT = 1_200;
const NUDGE_THRESHOLD = 50;

const getStepName = (stepIndex: number): TReviewStepName | 'unknown' =>
  REVIEW_STEP_NAMES[stepIndex - 1] ?? 'unknown';

const ReviewTemplate: React.FC<IReviewTemplate> = props => {
  if (!props.isOpen) return null;
  return <ReviewTemplateInner key={props.bookingId} {...props} />;
};

const ReviewTemplateInner: React.FC<IReviewTemplate> = ({ bookingId, isOpen, onClose }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { isAboveDesktop } = useBreakpointContext();
  const [images, setImages] = useState<string[]>([]);
  const [descriptionExperience, setDescriptionExperience] = useState('');
  const [displayTextInputError, setDisplayTextInputError] = useState(false);
  const booking = useSelector(bookingDetailsSelector);
  const isFetchingBooking = useSelector(loadBookingDetailsSelector);
  const isAuthenticated = useSelector(selectAuthenticated);
  const isFetchingUser = useSelector(getIsFetchingUser);
  const authenticatedUser = useSelector(getAuthenticatedUser);
  const currentReview = useSelector(getCurrentReview);
  const currentScore = currentReview.data?.score;
  const currentText = currentReview.data?.text;
  const currentImages = currentReview.data?.images;
  const [rating, setRating] = useState(0);
  const [communicationRating, setCommunicationRating] = useState(0);
  const [cleanlinessRating, setCleanlinessRating] = useState(0);
  const [maintenanceRating, setMaintenanceRating] = useState(0);
  const [valueRating, setValueRating] = useState(0);
  const [listingAccuracyRating, setListingAccuracyRating] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const currentStepRef = useRef(1);

  const isCampground = !!booking?.rental_summary?.campsite_summary;
  const rentalType = (
    isCampground
      ? booking?.rental_summary?.campsite_summary?.type || ERentalType.CAMPSITE
      : ERentalType.RV
  ) as ERentalType;
  const isNewReview = typeof currentReview.data?.id === 'undefined';

  const isFromEmail =
    typeof location !== 'undefined' && Boolean(getUrlParams(location.href)['token']);
  const source = isFromEmail ? EReviewSource.EMAIL : EReviewSource.TRIPS;

  const funnelProps = useMemo(
    () => ({
      flow: EReviewFlow.GUEST_TO_HOST,
      bookingID: bookingId,
      source,
      rentalType,
      isCampground,
      isNewReview,
    }),
    [bookingId, source, rentalType, isCampground, isNewReview],
  );

  const didFireOpened = useRef(false);
  const terminalEventFired = useRef(false);
  const stepsCompleted = useRef<Set<number>>(new Set());
  const fireStepCompleted = (stepIndex: number) => {
    if (!didFireOpened.current) return;
    if (stepsCompleted.current.has(stepIndex)) return;
    stepsCompleted.current.add(stepIndex);
    trackReviewStepCompleted({
      ...funnelProps,
      stepIndex,
      stepName: getStepName(stepIndex),
    });
  };

  const bookingLoaded = booking != null && booking.id === bookingId;
  const authorizedToReview = Boolean(
    isAuthenticated &&
      !isFetchingUser &&
      authenticatedUser?.id &&
      bookingLoaded &&
      booking?.renter_id &&
      authenticatedUser.id === booking.renter_id,
  );
  const [fetchAttempts, setFetchAttempts] = useState(0);
  const reviewLoaded = !currentReview.isFetching && currentReview.data?.booking_id === bookingId;

  useReviewExpiredMessage({
    booking,
    hasReviewed: reviewLoaded ? Boolean(currentReview.data?.id) : null,
  });

  const showFetchError =
    fetchAttempts >= 2 && !reviewLoaded && !currentReview.isFetching && !terminalEventFired.current;

  // Kept off authorizedToReview so authGuard's "not authorized" copy
  // isn't shown for expired bookings — useReviewExpiredMessage handles that.
  const canReview = booking?.can_renter_review !== false;
  useEffect(() => {
    if (
      didFireOpened.current ||
      !reviewLoaded ||
      !authorizedToReview ||
      isFetchingBooking ||
      !canReview
    ) {
      return;
    }
    didFireOpened.current = true;
    trackReviewModalOpened(funnelProps);
  }, [reviewLoaded, authorizedToReview, isFetchingBooking, canReview, funnelProps]);

  const handleClose = () => {
    if (didFireOpened.current && !terminalEventFired.current && !isSubmitting) {
      terminalEventFired.current = true;
      const step = currentStepRef.current;
      trackReviewStepAbandoned({
        ...funnelProps,
        stepIndex: step,
        stepName: getStepName(step),
      });
    }
    onClose();
  };

  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  useEffect(() => {
    if (!isAuthenticated && !isFetchingUser) {
      triggerLoginModal();
    }
  }, [isAuthenticated, isFetchingUser]);

  useEffect(() => {
    if (
      isOpen &&
      isAuthenticated &&
      authenticatedUser?.id !== undefined &&
      bookingId > 0 &&
      !reviewLoaded &&
      !currentReview.isFetching &&
      fetchAttempts < 2
    ) {
      setFetchAttempts(n => n + 1);
      dispatch(fetchReviewByBookingId(bookingId, authenticatedUser.id));
    }
  }, [
    bookingId,
    dispatch,
    isAuthenticated,
    authenticatedUser,
    isOpen,
    reviewLoaded,
    currentReview.isFetching,
    fetchAttempts,
  ]);

  useEffect(() => {
    if (reviewLoaded) setFetchAttempts(0);
  }, [reviewLoaded]);

  useEffect(() => {
    if (!reviewLoaded) return;
    if (currentScore) setRating(Number(currentScore));
    if (currentText) setDescriptionExperience(currentText);
    if (currentImages) setImages(currentImages);
    const cr = currentReview.data;
    if (cr?.communication_score) setCommunicationRating(cr.communication_score);
    if (cr?.cleanliness_score) setCleanlinessRating(cr.cleanliness_score);
    if (cr?.mechanical_score) setMaintenanceRating(cr.mechanical_score);
    if (cr?.value_score) setValueRating(cr.value_score);
    if (cr?.listing_score) setListingAccuracyRating(cr.listing_score);
  }, [currentScore, currentText, currentImages, currentReview.data, reviewLoaded]);

  const overallQualitativeLabel = intl.formatMessage(
    {
      id: 'euTaM6',
      defaultMessage:
        '{rating, select, 1 {Spark-worthy} 2 {Kindling-worthy} 3 {Flame-worthy} 4 {Campfire-worthy} 5 {Bonfire-worthy} other {}}',
    },
    { rating },
  );

  const step1Header = intl.formatMessage({
    id: 'DgVFH4',
    defaultMessage: 'Overall, how was it?',
  });
  const step1Body = intl.formatMessage({
    id: 'lKSV+w',
    defaultMessage: 'Be fair, this stays on their profile.',
  });
  const step2Header = intl.formatMessage({
    id: 'kxP3q8',
    defaultMessage: 'How was the trip?',
  });
  const step2Body = intl.formatMessage({
    id: 'yTDLQV',
    defaultMessage:
      'The host will have a chance to review you, too. Reviews stay hidden until you both submit, or 14 days after the trip ends.',
  });
  const step3Header = intl.formatMessage({
    defaultMessage: 'Describe your experience',
    id: 'QyNZqS',
  });
  const step3Body = intl.formatMessage({
    id: '0vHvyV',
    defaultMessage: 'Add a few words about your trip. Your review will be public on the listing.',
  });
  const step4Header = intl.formatMessage({
    id: 'AdtDDx',
    defaultMessage: 'Add a photo of your trip',
  });
  const step4Body = intl.formatMessage({
    id: 'pSNinR',
    defaultMessage:
      'A sunset, a trailhead, the rig parked somewhere quiet. Fellow adventurers would love to see it.',
  });

  const experiencePlaceholderText = intl.formatMessage({
    defaultMessage: 'Tell us about your experience in the RV and your experience with the host',
    id: 'vXBo/k',
  });
  const shortEntryNudge = intl.formatMessage({
    id: 'hLC3lo',
    defaultMessage: 'Add a little more to help future renters.',
  });
  const minCharsError = intl.formatMessage(
    { id: 'anHlOi', defaultMessage: 'Please write at least {min} characters.' },
    { min: CHAR_MIN },
  );

  const photoPlaceHolder = intl.formatMessage({ defaultMessage: 'Add photos', id: 'ex9l8g' });

  const continueLabel = intl.formatMessage({ defaultMessage: 'Continue', id: 'acrOoz' });
  const submitLabel = intl.formatMessage({ defaultMessage: 'Submit', id: 'wSZR47' });
  const skipLabel = intl.formatMessage({ defaultMessage: 'Cancel', id: '47FYwb' });
  const rateEachToContinueLabel = intl.formatMessage({
    defaultMessage: 'Rate each to continue',
    id: '6AGona',
  });
  const addWordsToContinueLabel = intl.formatMessage({
    defaultMessage: 'Add a few words to continue',
    id: 'PtbNwS',
  });

  const trimmedNoteLength = descriptionExperience.trim().length;
  const step1CanContinue = rating > 0;
  const step2CanContinue =
    communicationRating > 0 &&
    cleanlinessRating > 0 &&
    maintenanceRating > 0 &&
    valueRating > 0 &&
    listingAccuracyRating > 0;
  const step3CanContinue = trimmedNoteLength >= CHAR_MIN;

  const primaryLabel = (() => {
    if (currentStep === 1) return continueLabel;
    if (currentStep === 2) return step2CanContinue ? continueLabel : rateEachToContinueLabel;
    if (currentStep === 3) return step3CanContinue ? continueLabel : addWordsToContinueLabel;
    return submitLabel;
  })();

  const isLoadingContent =
    isFetchingUser || currentReview.isFetching || isFetchingBooking || !reviewLoaded;

  const primaryDisabled = (() => {
    if (isSubmitting || isLoadingContent) return true;
    if (currentStep === 1) return !step1CanContinue;
    if (currentStep === 2) return !step2CanContinue;
    if (currentStep === 3) return !step3CanContinue;
    return false;
  })();

  const onChangeDescription = (nextValue: string) => {
    setDescriptionExperience(nextValue.slice(0, CHAR_LIMIT));
    setDisplayTextInputError(false);
  };

  const onUploadImage = (imagesUploaded: string[]) => {
    setImages([...images, ...imagesUploaded]);
  };

  const onDeleteImage = (imageToFilter: string) => {
    setImages(images.filter(image => image !== imageToFilter));
  };

  const submitReview = async () => {
    setDisplayTextInputError(false);

    if (!rating) {
      setCurrentStep(1);
      return;
    }
    if (trimmedNoteLength < CHAR_MIN) {
      setDisplayTextInputError(true);
      setCurrentStep(3);
      return;
    }

    const { id: booking_id, rental_id } = booking || {};

    let reviewData: Partial<IReview> = {
      id: 0,
      booking_id,
      cleanliness_score: cleanlinessRating,
      communication_score: communicationRating,
      exclude_from_rating: false,
      images,
      listing_score: listingAccuracyRating,
      mechanical_score: maintenanceRating,
      owner_can_respond: false,
      published: false,
      rental_id,
      review_images: [],
      reviewer_id: authenticatedUser?.id,
      score: rating,
      tags: [],
      text: descriptionExperience,
      user_id: null,
      user_recommend: true,
      value_score: valueRating,
    };

    const currentReviewData = currentReview.data;
    const isNew = typeof currentReviewData?.id === 'undefined';

    countStepOneReviewFieldChanged(reviewData, currentReviewData);
    if (!isNew) {
      reviewData = {
        ...reviewData,
        ...pick(currentReviewData, ...(['id', 'user_recommend', 'tags'] as (keyof IReview)[])),
      };
      reviewData.tags = currentReviewData?.rental_tags;
    }

    const requestUrl = `${getCoreApi()}/reviews${reviewData.id ? `/${reviewData.id}` : ''}`;

    await apiRequest<IReview>(
      { url: requestUrl, data: reviewData, method: reviewData.id ? 'PATCH' : 'POST' },
      true,
      false,
    );

    if (isNew) {
      trackReviewRated({
        bookingID: bookingId,
        ratingScore: reviewData.score,
        source,
        rentalType,
        isCampground,
      });
      trackReviewSubmit({
        bookingID: bookingId,
        ratingScore: reviewData.score,
        communicationScore: reviewData.communication_score,
        cleanlinessScore: reviewData.cleanliness_score,
        mechanicalScore: reviewData.mechanical_score,
        valueScore: reviewData.value_score,
        listingAccuracyScore: reviewData.listing_score,
        experienceCharacterCount: descriptionExperience.length,
        totalImagesUploaded: images.length,
        isRecommended: reviewData.user_recommend,
        tags: reviewData.tags?.map(tag => tag.slug) || [],
        rentalType,
        isCampground,
      });
    }

    if (authenticatedUser?.id) {
      dispatch(fetchReviewByBookingId(bookingId, authenticatedUser.id));
    }
    dispatch(fetchBooking(String(bookingId), true));

    fireStepCompleted(TOTAL_STEPS);
    terminalEventFired.current = true;
    onClose();
  };

  const handlePrimary = async () => {
    if (currentStep < TOTAL_STEPS) {
      fireStepCompleted(currentStep);
      setCurrentStep(currentStep + 1);
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await submitReview();
    } finally {
      setIsSubmitting(false);
    }
  };

  const authGuard = (() => {
    if (authorizedToReview) return null;
    if (!isAuthenticated && !isFetchingUser) {
      return intl.formatMessage({
        defaultMessage: 'You must be logged-in to perform this action.',
        id: '4ABl2q',
      });
    }
    if (isAuthenticated && authenticatedUser?.id !== booking?.renter_id) {
      return intl.formatMessage({
        defaultMessage: 'You are not authorized to perform this action.',
        id: 'VZ0bQ3',
      });
    }
    return null;
  })();

  const [stepHeader, stepBody] = (() => {
    if (currentStep === 1) return [step1Header, step1Body];
    if (currentStep === 2) return [step2Header, step2Body];
    if (currentStep === 3) return [step3Header, step3Body];
    return [step4Header, step4Body];
  })();

  const content = (
    <>
      <Modal.Actions />

      <Modal.Header>
        <div className="flex w-full mb-4 max-w-[240px] gap-1">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full ${
                i < currentStep ? 'bg-gray-900' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <Heading variant={EHeadingStyleVariant.H5} className="mb-1">
          {stepHeader}
        </Heading>
        <Text variant={ETextStyleVariant.MediumRegular} className="text-gray-600">
          {stepBody}
        </Text>
      </Modal.Header>

      <Modal.Content>
        {authGuard ? (
          <HelpText
            className="px-4 py-2 m-2"
            text={authGuard}
            variant={EHelpTextVariant.Error}
            showIcon
          />
        ) : showFetchError ? (
          <HelpText
            className="px-4 py-2 m-2"
            text={intl.formatMessage({
              id: 'HuFGgs',
              defaultMessage:
                "We couldn't load this review. Please close this window and try again.",
              description: 'Renter review modal > fetch failed error message',
            })}
            variant={EHelpTextVariant.Error}
            showIcon
          />
        ) : (
          <div className="flex flex-col min-h-[280px]">
            {isLoadingContent && (
              <div className="flex items-center justify-center flex-1">
                <Loading />
              </div>
            )}
            {!isLoadingContent && currentStep === 1 && (
              <section className="flex flex-col items-center pt-6 pb-4 gap-4">
                <StarRating
                  minRating={rating}
                  onClick={e => {
                    const next = parseInt(e.currentTarget.value, 10);
                    if (!Number.isNaN(next)) setRating(next);
                  }}
                  disabledMin={0}
                  variant="text-green-500"
                  iconSize={[48, 48]}
                  className="gap-2"
                  outlineForUnselected
                  required
                />
                {rating > 0 && (
                  <Text
                    variant={ETextStyleVariant.SmallRegular}
                    className="text-center text-gray-600">
                    {overallQualitativeLabel}
                  </Text>
                )}
              </section>
            )}

            {!isLoadingContent && currentStep === 2 && (
              <section className="flex flex-col pt-2 gap-4">
                <CategoryRating
                  title={intl.formatMessage({ defaultMessage: 'Communication', id: 'n5D/Ku' })}
                  min={communicationRating}
                  setFunc={setCommunicationRating}
                  starVariant="text-green-500"
                  hideRatingText
                  outlineForUnselected
                  required
                />
                <CategoryRating
                  title={intl.formatMessage({ defaultMessage: 'Cleanliness', id: 'VgbIrO' })}
                  min={cleanlinessRating}
                  setFunc={setCleanlinessRating}
                  starVariant="text-green-500"
                  hideRatingText
                  outlineForUnselected
                  required
                />
                <CategoryRating
                  title={intl.formatMessage({ id: 'dudqv/', defaultMessage: 'Maintenance' })}
                  min={maintenanceRating}
                  setFunc={setMaintenanceRating}
                  starVariant="text-green-500"
                  hideRatingText
                  outlineForUnselected
                  required
                />
                <CategoryRating
                  title={intl.formatMessage({ id: 'GufXy5', defaultMessage: 'Value' })}
                  min={valueRating}
                  setFunc={setValueRating}
                  starVariant="text-green-500"
                  hideRatingText
                  outlineForUnselected
                  required
                />
                <CategoryRating
                  title={intl.formatMessage({ id: '6ZK/bz', defaultMessage: 'Listing accuracy' })}
                  min={listingAccuracyRating}
                  setFunc={setListingAccuracyRating}
                  starVariant="text-green-500"
                  hideRatingText
                  outlineForUnselected
                  required
                />
              </section>
            )}

            {!isLoadingContent && currentStep === 3 && (
              <section className="tracking-normal highlight">
                <Textarea
                  controlled
                  name="experience-description"
                  ariaLabel={step3Header}
                  placeholder={experiencePlaceholderText}
                  inputValue={descriptionExperience}
                  onChange={onChangeDescription}
                  error={displayTextInputError && trimmedNoteLength < CHAR_MIN}
                  helpText={
                    displayTextInputError && trimmedNoteLength < CHAR_MIN ? minCharsError : ''
                  }
                />
                {trimmedNoteLength >= CHAR_MIN && trimmedNoteLength < NUDGE_THRESHOLD && (
                  <Alert
                    className="mt-2"
                    variant={EAlertVariant.Alert}
                    size={EAlertSize.Small}
                    customIcon={GeneralBulbIcon}
                    content={shortEntryNudge}
                  />
                )}
                <p className="mt-2 text-sm text-right text-gray-600">
                  {trimmedNoteLength} / {CHAR_LIMIT.toLocaleString()}
                </p>
              </section>
            )}

            {!isLoadingContent && currentStep === 4 && (
              <section className="tracking-normal highlight">
                <ImageUploader
                  label={photoPlaceHolder}
                  rentalId={bookingId}
                  images={images}
                  onUploadImage={onUploadImage}
                  onDeleteImage={onDeleteImage}
                  fullWidth
                />
              </section>
            )}
          </div>
        )}
      </Modal.Content>

      <Modal.Footer variant={EModalFooterVariants.Actions}>
        <LinkButton label={skipLabel} onClick={handleClose} disabled={isSubmitting} />
        <Button
          data-testid="review-submit-btn"
          variant={EButtonColorVariant.Primary}
          label={primaryLabel}
          disabled={primaryDisabled}
          onClick={handlePrimary}
        />
      </Modal.Footer>
    </>
  );

  if (isAboveDesktop) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size={EModalSize.Medium}
        withOverlay
        isDismissable={!isSubmitting}>
        {content}
      </Modal>
    );
  }

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={handleClose}
      variant={EModalVariants.Bottom}
      withOverlay
      isDismissable={!isSubmitting}>
      {content}
    </AnimatedModal>
  );
};

export default ReviewTemplate;
